import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';
import Markdown from 'preact-markdown';

import HelpText from '../../components/gui/helpText';

const MAX_PROMPT_LENGTH = 1000;
// The lookup runs in the background on the server (20-90 s). The drawer polls
// the entry until it is completed or failed.
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_MS = 180000;
const PENDING_TTL_MS = 10 * 60 * 1000;

function isStalePending(entry, now = Date.now()) {
    return entry.status === 'pending'
        && now - new Date(entry.createdDate).getTime() > PENDING_TTL_MS;
}

function newestFirst(entries) {
    return [...entries].sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
}

const QUICK_PROMPTS = [
    {
        key: 'consumables',
        icon: 'fa-duotone fa-box-open',
        label: <Text id='part.ai-lookup-quick-consumables'>Consumables and accessories</Text>,
        prompt: 'Hvilke forbruksvarer og tilbehør passer til denne delen, og hvor får jeg kjøpt dem i Norge?',
    },
    {
        key: 'support',
        icon: 'fa-duotone fa-life-ring',
        label: <Text id='part.ai-lookup-quick-support'>Support and manuals</Text>,
        prompt: 'Finn brukerstøtte, brukermanual, drivere og garantiinformasjon for denne delen.',
    },
    {
        key: 'spares',
        icon: 'fa-duotone fa-screwdriver-wrench',
        label: <Text id='part.ai-lookup-quick-spares'>Spare parts</Text>,
        prompt: 'Hvor får jeg kjøpt reservedeler til denne delen i Norge?',
    },
    {
        key: 'used-price',
        icon: 'fa-duotone fa-tag',
        label: <Text id='part.ai-lookup-quick-used-price'>Used price</Text>,
        prompt: 'Hva er en realistisk bruktpris for denne delen i Norge i dag?',
    },
];

@observer
class PartAiLookup extends Component {
    constructor(props) {
        super(props);
        const answers = newestFirst(this.storedLookups());
        // A lookup started earlier (drawer closed, page reloaded) may still be
        // running on the server: keep polling it instead of spinning forever.
        const open = answers.find(a => a.status === 'pending' && !isStalePending(a));
        this.state = {
            prompt: '',
            loading: false,
            error: null,
            errorIsLimit: false,
            errorIsRunning: false,
            answers,
            remainingToday: null,
            pollingUuidv4: open ? open.uuidv4 : null,
            pollSlow: false,
            confirmDeleteUuidv4: null,
            deletingUuidv4: null,
            deleteErrorUuidv4: null,
        };
        this.pollTimer = null;
        this.pollStartedAt = open ? new Date(open.createdDate).getTime() : 0;
        this.unmounted = false;
    }

    componentDidMount() {
        const { pollingUuidv4 } = this.state;
        if (pollingUuidv4) {
            this.schedulePoll(pollingUuidv4);
        }
    }

    componentWillUnmount() {
        this.unmounted = true;
        this.stopPolling();
    }

    // The store's part is only ours when the ids match: nested part drawers
    // (similar parts) load other parts into the same store. The id prop may be
    // a string (read from a data attribute in the part list), so compare numerically.
    isStorePart = () => {
        const { id } = this.props;
        const { partStore } = this.props.stores;
        const part = partStore?.part;
        return !!part && Number(part.id) > 0 && Number(part.id) === Number(id);
    }

    storedLookups = () => {
        const { partStore } = this.props.stores;
        if (!this.isStorePart() || !Array.isArray(partStore.part.aiLookups)) {
            return [];
        }
        return partStore.part.aiLookups;
    }

    onPromptInput = e => {
        this.setState({ prompt: e.target.value });
    }

    onQuickPrompt = e => {
        const { prompt } = e.currentTarget.dataset;
        this.setState({
            prompt, error: null, errorIsLimit: false, errorIsRunning: false,
        });
    }

    stopPolling = () => {
        if (this.pollTimer) {
            clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }
    }

    schedulePoll = uuidv4 => {
        this.stopPolling();
        this.pollTimer = setTimeout(() => this.poll(uuidv4), POLL_INTERVAL_MS);
    }

    poll = async uuidv4 => {
        const { id } = this.props;
        const { partStore } = this.props.stores;
        if (Date.now() - this.pollStartedAt > POLL_MAX_MS) {
            this.pollTimer = null;
            this.setState({ pollingUuidv4: null, pollSlow: true });
            return;
        }
        let entry = null;
        try {
            const response = await partStore.aiLookupStatus(id, uuidv4);
            entry = (response?.status === 200 && response.data) ? response.data : null;
        } catch (err) {
            entry = null;
        }
        if (this.unmounted) {
            return; // the drawer closed while the request was in flight
        }
        if (!entry || entry.status === 'pending') {
            this.schedulePoll(uuidv4);
            return;
        }
        this.pollTimer = null;
        this.setState(prevState => ({
            pollingUuidv4: null,
            answers: prevState.answers.map(a => (a.uuidv4 === uuidv4 ? { ...a, ...entry } : a)),
        }), () => {
            if (!this.isStorePart()) {
                return;
            }
            const rest = this.storedLookups().filter(a => a.uuidv4 !== uuidv4);
            partStore.updateObjectKeyValue('part', 'aiLookups', [...rest, entry]);
        });
    }

    onSubmit = async e => {
        if (e) e.preventDefault();
        const { id } = this.props;
        const { partStore } = this.props.stores;
        const { loading } = this.state;
        const prompt = this.state.prompt.trim();
        if (loading || !prompt || !id) {
            return;
        }
        this.setState({
            loading: true, error: null, errorIsLimit: false, errorIsRunning: false, pollSlow: false,
        });
        try {
            const response = await partStore.aiLookup(id, prompt);
            const status = response?.status || 0;
            if (status === 429) {
                this.setState({ loading: false, error: 'limit', errorIsLimit: true });
                return;
            }
            if (status === 409) {
                this.setState({ loading: false, error: 'running', errorIsRunning: true });
                return;
            }
            if (status !== 202 || !response?.data?.uuidv4) {
                this.setState({ loading: false, error: 'failed', errorIsLimit: false });
                return;
            }
            const { data } = response;
            const pending = {
                uuidv4: data.uuidv4,
                status: 'pending',
                prompt,
                sources: [],
                createdDate: new Date().toISOString(),
            };
            this.pollStartedAt = Date.now();
            this.setState(prevState => ({
                loading: false,
                prompt: '',
                answers: [pending, ...prevState.answers],
                remainingToday: typeof data.remainingToday === 'number' ? data.remainingToday : prevState.remainingToday,
                pollingUuidv4: data.uuidv4,
            }));
            this.schedulePoll(data.uuidv4);
        } catch (err) {
            this.setState({ loading: false, error: 'failed', errorIsLimit: false });
        }
    }

    onDeleteClick = e => {
        const { uuidv4 } = e.currentTarget.dataset;
        this.setState({ confirmDeleteUuidv4: uuidv4, deleteErrorUuidv4: null });
    }

    onDeleteCancel = () => {
        this.setState({ confirmDeleteUuidv4: null });
    }

    onDeleteConfirm = async e => {
        const { uuidv4 } = e.currentTarget.dataset;
        const { id } = this.props;
        const { partStore } = this.props.stores;
        if (this.state.deletingUuidv4) {
            return;
        }
        this.setState({ deletingUuidv4: uuidv4, deleteErrorUuidv4: null });
        let ok = false;
        try {
            const response = await partStore.aiLookupDelete(id, uuidv4);
            ok = response?.status === 200;
        } catch (err) {
            ok = false;
        }
        if (this.unmounted) {
            return;
        }
        if (!ok) {
            this.setState({ deletingUuidv4: null, confirmDeleteUuidv4: null, deleteErrorUuidv4: uuidv4 });
            return;
        }
        this.setState(prevState => ({
            deletingUuidv4: null,
            confirmDeleteUuidv4: null,
            answers: prevState.answers.filter(a => a.uuidv4 !== uuidv4),
        }), () => {
            if (!this.isStorePart()) {
                return;
            }
            const rest = this.storedLookups().filter(a => a.uuidv4 !== uuidv4);
            partStore.updateObjectKeyValue('part', 'aiLookups', rest);
        });
    }

    renderDelete = answer => {
        const { confirmDeleteUuidv4, deletingUuidv4 } = this.state;
        const deleting = deletingUuidv4 === answer.uuidv4;
        if (confirmDeleteUuidv4 === answer.uuidv4 || deleting) {
            return (
                <span class='ms-2 text-nowrap'>
                    <button
                        type='button'
                        class='btn btn-sm btn-danger part-ai-delete-confirm'
                        data-uuidv4={answer.uuidv4}
                        onClick={this.onDeleteConfirm}
                        disabled={deleting}
                    >
                        <i class={`fa-duotone ${deleting ? 'fa-spinner fa-spin' : 'fa-trash'} me-1`} />
                        <Text id='part.ai-lookup-delete-confirm'>Confirm delete</Text>
                    </button>
                    <button
                        type='button'
                        class='btn btn-sm btn-link text-body-secondary part-ai-delete-cancel'
                        onClick={this.onDeleteCancel}
                        disabled={deleting}
                    >
                        <Text id='part.ai-lookup-delete-cancel'>Cancel</Text>
                    </button>
                </span>
            );
        }
        return (
            <Localizer>
                <button
                    type='button'
                    class='btn btn-sm btn-link text-body-secondary part-ai-delete ms-2'
                    data-uuidv4={answer.uuidv4}
                    onClick={this.onDeleteClick}
                    title={<Text id='part.ai-lookup-delete'>Delete answer</Text>}
                >
                    <i class='fa-duotone fa-trash' />
                </button>
            </Localizer>
        );
    }

    renderAnswer = answer => {
        const { deleteErrorUuidv4 } = this.state;
        const failed = answer.status === 'failed' || isStalePending(answer);
        const pending = answer.status === 'pending' && !failed;
        const sources = Array.isArray(answer.sources) ? answer.sources : [];
        const cardClass = `card mb-3 part-ai-answer${pending ? ' part-ai-answer-pending' : ''}${failed ? ' part-ai-answer-failed' : ''}`;
        return (
            <div class={cardClass} key={answer.uuidv4}>
                <div class='card-header d-flex flex-row align-items-start'>
                    <i class='fa-duotone fa-comment-question me-2 mt-1' />
                    <span class='text-body flex-grow-1'>{answer.prompt}</span>
                    {answer.userName && <small class='text-body-secondary ms-2 text-nowrap'>
                        <Text id='part.ai-lookup-asked-by' fields={{ name: answer.userName }}>Asked by {answer.userName}</Text>
                    </small>}
                    {!pending && this.renderDelete(answer)}
                </div>
                <div class='card-body'>
                    {deleteErrorUuidv4 === answer.uuidv4 && <div class='alert alert-danger part-ai-delete-error small mb-2'>
                        <i class='fa-duotone fa-triangle-exclamation me-2' />
                        <Text id='part.ai-lookup-delete-error'>Could not delete the answer. Please try again.</Text>
                    </div>}
                    {pending && <div class='text-body-secondary'>
                        <i class='fa-duotone fa-spinner fa-spin me-2' />
                        <Text id='part.ai-lookup-searching'>Searching the web...</Text>
                    </div>}
                    {failed && <div class='text-danger'>
                        <i class='fa-duotone fa-triangle-exclamation me-2' />
                        {answer.status === 'failed'
                            ? (answer.error || <Text id='part.ai-lookup-error'>The AI lookup failed. Please try again.</Text>)
                            : <Text id='part.ai-lookup-aborted'>The lookup was interrupted.</Text>}
                    </div>}
                    {!pending && !failed && <Markdown markdown={answer.text || ''} />}
                    {!pending && !failed && sources.length > 0 && <div class='part-ai-sources mt-3'>
                        <div class='small text-body-secondary mb-1'>
                            <i class='fa-duotone fa-link me-1' />
                            <Text id='part.ai-lookup-sources'>Sources</Text>
                        </div>
                        <ul class='list-unstyled small mb-0'>
                            {sources.map(source => (
                                <li key={source.url} class='text-truncate'>
                                    <a href={source.url} target='_blank' rel='noopener noreferrer'>{source.title || source.url}</a>
                                    {source.pageAge && <span class='text-body-secondary ms-2'>{source.pageAge}</span>}
                                </li>
                            ))}
                        </ul>
                    </div>}
                </div>
            </div>
        );
    }

    render() {
        const { title } = this.props;
        const {
            prompt, loading, error, errorIsLimit, errorIsRunning, answers, remainingToday, pollSlow,
        } = this.state;
        const canSend = !loading && prompt.trim().length > 0;

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                    <div class='font-weight-lighter px-3 box-header d-flex flex-row align-items-center'>
                        <i class='fa-duotone fa-wand-magic-sparkles me-2' />
                        <Text id='part.ai-lookup-title'>Ask AI</Text>
                        {title && <span class='ms-2 text-body-secondary'>- {title}</span>}
                    </div>

                    <div class='px-3 mt-3'>
                        <div class='d-flex flex-row flex-wrap gap-2 mb-2'>
                            {QUICK_PROMPTS.map(quick => (
                                <button
                                    type='button'
                                    key={quick.key}
                                    class='btn btn-sm btn-outline-secondary rounded-pill part-ai-quick-prompt'
                                    data-prompt={quick.prompt}
                                    onClick={this.onQuickPrompt}
                                    disabled={loading}
                                >
                                    <i class={`${quick.icon} me-1`} />
                                    {quick.label}
                                </button>
                            ))}
                        </div>

                        <form onSubmit={this.onSubmit}>
                            <Localizer>
                                <textarea
                                    class='form-control'
                                    rows={3}
                                    maxLength={MAX_PROMPT_LENGTH}
                                    value={prompt}
                                    onInput={this.onPromptInput}
                                    disabled={loading}
                                    placeholder={<Text id='part.ai-lookup-placeholder'>E.g. Find paper for my printer in Norway</Text>}
                                />
                            </Localizer>
                            <div class='d-flex flex-row align-items-center justify-content-between mt-2'>
                                <small class='text-body-secondary'>
                                    {typeof remainingToday === 'number' && <Text id='part.ai-lookup-remaining' fields={{ count: remainingToday }}>
                                        {remainingToday} lookups left today
                                    </Text>}
                                </small>
                                <button type='submit' class='btn btn-primary rounded-pill part-ai-send' disabled={!canSend}>
                                    <i class={`fa-duotone ${loading ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-2`} />
                                    {loading
                                        ? <Text id='part.ai-lookup-searching'>Searching the web...</Text>
                                        : <Text id='part.ai-lookup-send'>Ask</Text>}
                                </button>
                            </div>
                        </form>
                        <HelpText>
                            <Text id='part.ai-lookup-help'>The AI searches the web with the part's name, brand and part numbers and prefers Norwegian shops and sources.</Text>
                        </HelpText>

                        {error && errorIsLimit && <div class='alert alert-warning part-ai-error mt-3'>
                            <i class='fa-duotone fa-hourglass-half me-2' />
                            <Text id='part.ai-lookup-limit'>You have reached the daily limit for AI lookups. Try again tomorrow.</Text>
                        </div>}
                        {error && errorIsRunning && <div class='alert alert-info part-ai-running mt-3'>
                            <i class='fa-duotone fa-spinner me-2' />
                            <Text id='part.ai-lookup-running'>A lookup is already running for this part.</Text>
                        </div>}
                        {error && !errorIsLimit && !errorIsRunning && <div class='alert alert-danger part-ai-error mt-3'>
                            <i class='fa-duotone fa-triangle-exclamation me-2' />
                            <Text id='part.ai-lookup-error'>The AI lookup failed. Please try again.</Text>
                        </div>}
                        {pollSlow && <div class='alert alert-secondary part-ai-slow mt-3'>
                            <i class='fa-duotone fa-clock me-2' />
                            <Text id='part.ai-lookup-slow'>This is taking longer than usual. The answer will appear here when it is ready.</Text>
                        </div>}
                    </div>

                    <div class='px-3 mt-3'>
                        {answers.map(this.renderAnswer)}
                        {answers.length > 0 && <div class='alert alert-secondary small'>
                            <i class='fa-duotone fa-circle-info me-2' />
                            <Text id='part.ai-lookup-disclaimer'>AI-generated answer based on web search. Prices and availability may have changed - check the shop before you buy.</Text>
                        </div>}
                    </div>
                </div>
            </div>
        );
    }
}

export default PartAiLookup;
